import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ExternalLink, ShoppingCart, Radio, UserRound } from "lucide-react";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";
import AbarytDateBar from "./AbarytDateBar";
import { useAuth } from "../context/AuthContext";

const COAT_OF_ARMS = "https://customer-assets.emergentagent.com/job_92e13f48-99c4-452a-b014-1be6a3b8b28b/artifacts/e3352bxk_Mock%20up%20Jonesaica%20Yah%E2%80%99shar%E2%80%99al.png";

const primaryItems = [
  { label: "Home", href: "/" },
  { label: "Accounts", href: "/portal" },
  { label: "Governance", href: "/governance" },
  { label: "Programs", href: "/programs" },
  { label: "Records & Publishing", href: "/music" },
  { label: "Live", href: "/live" },
];
const secondaryItems = [
  { label: "About", href: "/about" },
  { label: "Become YaSHaRUN", href: "/become-national" },
  { label: "Fight Fi Tash", href: "/brands/fightfitash" },
  { label: "Shaba", href: "/brands/shaba" },
  { label: "YaHUaH's Girls", href: "/YaHsgirls" },
  { label: "Free Resources", href: "/free-resources" },
  { label: "Media", href: "/media" },
  { label: "Shop", href: "/shop" },
  { label: "Donate", href: "/donate" },
  { label: "Contact", href: "/contact" },
  { label: "Yasharal Live Login", href: "/yasharal-live/login" },
];
const navItems = [...primaryItems, ...secondaryItems];

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [liveNow, setLiveNow] = useState(false);
  const location = useLocation();
  const { count, openCart } = useCart();
  const { isAuthenticated } = useAuth();
  const accountPath = isAuthenticated ? "/portal" : "/login";
  const accountLabel = isAuthenticated ? "My Account" : "Sign In";

  useEffect(() => {
    let alive = true;
    const loadLiveState = async () => {
      try {
        const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        const response = await fetch(`${backend}/api/communications/public-live`, { cache: "no-store" });
        const data = await response.json();
        if (alive) setLiveNow(Boolean(data?.is_live));
      } catch {
        if (alive) setLiveNow(false);
      }
    };
    loadLiveState();
    const timer = setInterval(loadLiveState, 10000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  return (
    <header className="sticky top-0 z-50 w-full" data-testid="header">
      <AbarytDateBar />
      <div className="gov-bar py-2 px-4 text-center hidden md:block">
        <span>Official Government Portal of The Kingdom of Yunah'Shach'Qal Yashar'al</span>
      </div>

      <nav className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img
                src={COAT_OF_ARMS}
                alt="Yunah'Shach'Qal Yashar'al Coat of Arms"
                className="h-12 md:h-16 w-auto"
              />
              <div className="hidden sm:block">
                <p className="font-heading text-lg md:text-xl font-bold text-slate-900 leading-tight">
                  Yunah'Shach'Qal Yashar'al
                </p>
                <p className="text-xs text-slate-600 uppercase tracking-wider">admin@yasharal.vip</p>
              </div>
            </Link>

            <div className="hidden xl:flex min-w-0 items-center gap-0.5">
              {primaryItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`px-3 py-2 text-sm font-medium rounded transition-colors ${
                    item.label === "Live" && liveNow
                      ? "bg-red-600 text-white hover:bg-red-700 shadow-sm"
                      : location.pathname === item.href
                      ? "text-kingdom-gold hover:bg-slate-100"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {item.label === "Live" && <Radio className={`inline w-3.5 h-3.5 mr-1 ${liveNow ? "animate-pulse" : ""}`} />}
                  {item.label}
                </Link>
              ))}

              <div className="relative group">
                <button type="button" className="px-3 py-2 text-sm font-medium text-slate-700 rounded hover:bg-slate-100 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600" aria-label="More navigation" aria-haspopup="true">More ▾</button>
                <div className="absolute right-0 top-full mt-1 w-52 bg-white border shadow-lg rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all">
                  {secondaryItems.map((item) => (
                    <Link key={item.href} to={item.href} className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100">
                      {item.label}
                    </Link>
                  ))}
                  <a
                    href="https://solutions.yahsharal.info/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                  >
                    Infrastructure / Solar
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>

              <Link to={accountPath} className="inline-flex shrink-0 items-center gap-1 rounded-md bg-royal-blue px-3 py-2 text-sm font-semibold text-white hover:bg-blue-800" data-testid="desktop-account-login-link">
                <UserRound className="h-4 w-4" /> {accountLabel}
              </Link>
              <Link to="/become-national">
                <Button className="btn-gold ml-2 px-3" data-testid="become-national-btn">Join the Nation</Button>
              </Link>
              <button
                onClick={openCart}
                className="relative p-2 ml-2 rounded hover:bg-slate-100 transition-colors"
                data-testid="header-cart-btn"
                aria-label="Open cart"
              >
                <ShoppingCart className="w-5 h-5 text-slate-700" />
                {count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-kingdom-gold text-royal-blue text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center" data-testid="header-cart-count">{count}</span>
                )}
              </button>
            </div>

            <div className="xl:hidden flex items-center gap-1">
              <Link to={accountPath} className="inline-flex items-center gap-1 rounded-md bg-royal-blue px-2.5 py-2 text-xs font-semibold text-white sm:text-sm" data-testid="mobile-account-login-link">
                <UserRound className="h-4 w-4" /> {accountLabel}
              </Link>
              <button onClick={openCart} className="relative p-2 rounded hover:bg-slate-100 transition-colors" data-testid="header-cart-btn-mobile" aria-label="Open cart">
                <ShoppingCart className="w-5 h-5 text-slate-700" />
                {count > 0 && <span className="absolute -top-0.5 -right-0.5 bg-kingdom-gold text-royal-blue text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{count}</span>}
              </button>
              <button className="p-2 rounded hover:bg-slate-100" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
                {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileOpen && (
          <div className="xl:hidden bg-royal-purple text-white">
            <div className="px-4 py-6 space-y-1">
              <Link to={accountPath} onClick={() => setMobileOpen(false)} className="block rounded-md bg-white/15 px-3 py-3 text-lg font-bold text-white">{accountLabel}</Link>
              <Link to="/portal" onClick={() => setMobileOpen(false)} className="block rounded-md bg-white/10 px-3 py-3 text-lg font-semibold text-white">My Account</Link>
              <Link to="/music" onClick={() => setMobileOpen(false)} className="block rounded-md bg-white/10 px-3 py-3 text-lg font-semibold text-white">Record Label &amp; Publishing</Link>
              {navItems.filter(item => item.href !== "/music" && item.href !== "/members").map((item) => (
                <Link
                  key={item.label === "Live" && liveNow ? "● LIVE NOW" : item.label}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={`block py-3 px-3 text-lg rounded transition-colors ${item.label === "Live" && liveNow ? "bg-red-600 text-white font-bold" : location.pathname === item.href ? "text-kingdom-gold hover:bg-purple-800" : "hover:bg-purple-800"}`}
                >
                  {item.label}
                </Link>
              ))}
              <a
                href="https://solutions.yahsharal.info/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 py-3 px-3 text-lg hover:bg-purple-800 rounded"
              >
                Infrastructure / Solar
                <ExternalLink className="w-4 h-4" />
              </a>
              <div className="pt-4">
                <Link to="/become-national" onClick={() => setMobileOpen(false)}>
                  <Button className="w-full btn-gold" data-testid="mobile-join-btn">Join the Nation</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      <div className="bg-kingdom-gold text-royal-blue py-2 px-4 text-center text-sm">
        <span className="font-semibold">Nation App Available!</span>
        <span className="ml-2">Download on Apple App Store, Google Play & Microsoft Store</span>
        <span className="ml-2 text-xs opacity-70">(Coming Soon)</span>
      </div>
    </header>
  );
}

export default Header;
